import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EnvironmentsService } from '@references/shared/services/environments.service';
import { languages } from '@shared/helpers/languages.helper';
import { isPublicFormPath } from '@shared/helpers/public-form.helper';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { IUser } from '@shared/interfaces/user.interface';
import { ChunkRecoveryService } from '@shared/services/chunk-recovery.service';
import { CookieConsentService } from '@shared/services/cookie-consent.service';
import { UpdatesService } from '@shared/services/updates.service';
import { UsersService } from '@shared/services/users.service';
import { SessionStorage } from 'ngx-webstorage';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  @SessionStorage() private user: IUser;
  @SessionStorage() private hiringFirm: IHiringFirm;

  private languages: any[] = languages.map((l: any) => l.id);

  constructor(
    private readonly _environments: EnvironmentsService,
    private readonly _translate: TranslateService,
    private readonly _users: UsersService,
    private readonly _cookieConsent: CookieConsentService,
    _updates: UpdatesService, // eager-instantiate so SW poll/visibility wiring runs
    _chunkRecovery: ChunkRecoveryService // eager-instantiate so Router NavigationError subscription runs
  ) {
    this._translate.addLangs(this.languages);
    this._translate.setDefaultLang('en');

    // ATS sponge iframes must not show the consent banner; with no consent
    // recorded, no non-essential scripts load there either.
    const pathname = window.location.pathname;
    if (pathname.startsWith('/embed/') || pathname === '/embed') {
      this._cookieConsent.suppress();
    }

    this._environments.setScripts();
    this._environments.setTheme();
  }

  ngOnInit(): void {
    // Fetch current user to get the latest language preference.
    // Never on a public token-link form: /me is an authenticated call, and
    // a referee carrying a stale recruiter session would get a 401 that
    // pre-empts the form (their language comes from ?lng=/firm settings,
    // not a recruiter profile, anyway).
    if (this.user?.id && !isPublicFormPath()) {
      this._users.resolve().subscribe(
        (user: IUser) => {
          // Language hierarchy: user.language -> settings.language -> 'en'
          const lng = user?.language || this.hiringFirm?.settings?.language || 'en';
          this._environments.setLng(lng);
        }
      );
    } else {
      // Fallback to session storage if user data is already loaded
      // Language hierarchy: user.language -> settings.language -> 'en'
      const lng = this.user?.language || this.hiringFirm?.settings?.language || 'en';
      this._environments.setLng(lng);
    }
  }
}
